import { useCallback, useEffect, useState } from 'react';

import { getReadWorkspaceFileContentUrl } from '@/lib/api/generated/api';
import { getAccessToken, renewAccessToken } from '@/lib/auth/tokenStore';
import { env } from '@/lib/env';
import { reportFileReadFailure } from '../reportFileReadFailure';

/**
 * Why a file's bytes could not be turned into an object URL.
 *
 * Four cases rather than one, because the reader is owed a different sentence
 * for each (FR-067): a file that is not there, a file this reader may not read,
 * a session that has run out, and a read that failed on the way. The old single
 * `failed` boolean told every one of them that the page "could not be loaded",
 * which misattributes a transport failure to a missing file.
 *
 * `session-expired` is separate from `not-permitted` because the two ask the
 * reader for different things. A 401 means "not permitted" only if the identity
 * the request carried was still current; when the renewal that follows it
 * fails — or when there was no token to renew in the first place — the request
 * carried an identity that had run out or none at all, and the reader can fix
 * that by signing in again. Telling them they lack access to their own file
 * sends them to an administrator instead (F14, F25).
 */
export type FileReadFailureReason =
  'not-found' | 'not-permitted' | 'session-expired' | 'failed';

/**
 * A read that did not produce bytes, keeping what the server said.
 *
 * `status` is absent when there was no response at all — a connection that
 * never completed, an abort, or a `transform` that threw on the markup it was
 * given. That distinction is exactly what the log line needs.
 */
export class FileReadError extends Error {
  constructor(
    readonly reason: FileReadFailureReason,
    readonly status: number | undefined,
    options?: { cause?: unknown },
  ) {
    super(
      `file read ${reason}${status === undefined ? '' : ` (${String(status)})`}`,
      options,
    );
    this.name = 'FileReadError';
  }
}

/**
 * The caller's media type with `charset=utf-8` declared on it.
 *
 * A caller that already named a charset keeps it — the only caller that does so
 * would be declaring something it knows about its own transform's output, and
 * overruling that silently is worse than trusting it. Absent a type at all the
 * charset alone would be meaningless, so nothing is added.
 */
function withUtf8Charset(type: string | undefined): string | undefined {
  if (type === undefined) return undefined;
  if (type.toLowerCase().includes('charset=')) return type;
  return `${type};charset=utf-8`;
}

function reasonForStatus(status: number): FileReadFailureReason {
  if (status === 404) return 'not-found';
  if (status === 401 || status === 403) return 'not-permitted';
  return 'failed';
}

/**
 * An authenticated read of a file's bytes, as an object URL an `<img>` or the
 * PDF viewer's frame can use.
 *
 * `ImagePreview` used to point `<img src>` straight at the content endpoint. That
 * worked until access tokens became mandatory on the API (2026-08-31): an `<img>`
 * cannot carry an `Authorization` header, so every request 401'd and every
 * uploaded image rendered as a broken icon — with nothing in the UI saying why,
 * because a failed image is silent.
 *
 * So the bytes are fetched the same way the text preview fetches them — the same
 * raw `fetch`, the same renew-once-and-replay — and handed to the browser as a
 * blob. The original note argued against this because it keeps an image in memory
 * rather than streaming it; that trade was worth making when the alternative was
 * an image that renders, and is not worth making now that the alternative is one
 * that cannot.
 *
 * The URL is revoked when the preview closes or moves to another file, so opening
 * a folder full of screenshots does not retain every one of them.
 */
export function useFileObjectUrl(
  workspaceId: string,
  path: string,
  enabled: boolean,
  /**
   * The type to give the blob, whatever the server labelled it. The browser
   * picks its viewer from the blob's type, and a PDF stored as
   * `application/octet-stream` would download instead of showing.
   */
  type?: string,
  /**
   * Rewrite the fetched bytes, read as text, before the blob is made. Keep this
   * referentially stable — it is an effect dependency, so a fresh function
   * every render re-reads the file every render; pass a module-level `const`.
   * Absent, the bytes are used as-is.
   */
  transform?: (text: string) => string,
): {
  url: string | undefined;
  failed: boolean;
  /** Why it failed, for the message the reader is shown. `undefined` until it does. */
  failureReason: FileReadFailureReason | undefined;
  /** The cause, kept rather than discarded, so a caller can report it further. */
  error: unknown;
  /**
   * Read the file again (F15).
   *
   * The read is a one-shot effect with no query layer behind it, so a failure
   * is final until something asks again; bumping an attempt counter in the
   * effect's dependencies is what "again" means here. Offer it beside a
   * `'failed'` read only — the other three reasons are answers, and a control
   * that re-asks a settled question just fails a second time.
   */
  retry: () => void;
} {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<
    { reason: FileReadFailureReason; error: unknown } | undefined
  >(undefined);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let objectUrl: string | undefined;
    // The request outlives a fast click-through only until this flag flips; the
    // blob is still revoked below either way.
    let live = true;

    const send = (target: string) => {
      // Read inside the effect, never captured at render: a token read when the
      // component rendered may already have expired by the time this runs.
      const token = getAccessToken();
      return fetch(target, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    };

    void (async () => {
      const target = `${env.VITE_API_BASE_URL}${getReadWorkspaceFileContentUrl(workspaceId, { path })}`;
      // Carried out of the `try` so the report can name what the server
      // answered even when the failure happened after the response arrived.
      let status: number | undefined;
      try {
        let response = await send(target);
        /*
          Renew once and replay, exactly as `client.ts` and the text preview do
          — and remember when the renewal itself is what failed. Without that,
          the original 401 falls through to `reasonForStatus` and an expired
          session is reported to the reader as a permission denial (F14).
        */
        let renewalFailed = false;
        if (response.status === 401 && getAccessToken()) {
          const renewed = await renewAccessToken();
          if (renewed) response = await send(target);
          else renewalFailed = true;
        }
        status = response.status;
        if (!response.ok) {
          /*
            A 401 is a session expiry in both of the ways a session can be
            gone, not only the first (F25). The renewal path above runs only
            while a token exists, so a 401 arriving with *no* token — signed
            out, or a token the store already cleared after an earlier failed
            renewal — used to skip it and fall through to
            `reasonForStatus(401)` = `not-permitted`: the reader whose session
            lapsed was told they lack access to their own file and sent to an
            administrator, with the one recovery that would work (sign in
            again) never offered. A 401 means the request's identity was not
            accepted; "you are not allowed this file" is not something the
            server can be saying when the request carried no identity at all.
          */
          const sessionGone =
            response.status === 401 && (renewalFailed || !getAccessToken());
          throw new FileReadError(
            sessionGone ? 'session-expired' : reasonForStatus(response.status),
            response.status,
          );
        }
        let blob: Blob;
        if (transform !== undefined) {
          // The transform needs characters, not bytes: `response.text()` decodes
          // per the response's own charset, and the rewritten string is what the
          // blob is built from.
          const text = await response.text();
          /*
            `charset=utf-8`, always (F3/FR-066): by the time a JS string reaches
            `Blob` it is text, and `Blob` serializes it as UTF-8. Without the
            label the browser re-parses those UTF-8 bytes under its locale
            default (commonly windows-1252) and every non-ASCII character
            renders as mojibake. The caller's `type` names the media type; the
            charset is this path's to declare, because this path — not the
            caller — chose the encoding.
          */
          blob = new Blob([transform(text)], {
            type: withUtf8Charset(type),
          });
        } else {
          const fetched = await response.blob();
          blob =
            type !== undefined && fetched.type !== type
              ? new Blob([fetched], { type })
              : fetched;
        }
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setFailure(undefined);
      } catch (error) {
        /*
          Bound, logged and classified (F4/FR-067). This `catch` covers a
          refused read, a failed token renewal, a `transform` that threw on
          malformed markup, an abort and a failing `URL.createObjectURL` — the
          report is what tells those apart afterwards, and `reason` is what
          decides the sentence the reader gets.
        */
        const reason = error instanceof FileReadError ? error.reason : 'failed';
        /*
          Inside the `live` check, not before it (F16): a preview closed or
          switched while the read was out — and React 18's double-invoked
          effect in development — otherwise logged a warning and burned a
          Sentry event for an answer nobody was waiting for, which is how an
          error channel stops being read.
        */
        if (!live) return;
        reportFileReadFailure(
          reason,
          { workspaceId, path, status, reason },
          error,
        );
        setFailure({ reason, error });
      }
    })();

    /*
      Both resets live here rather than at the top of the effect: React's
      `set-state-in-effect` rule rejects a synchronous `setState` in an effect
      body, and it is right to — it cascades a render before the effect has done
      anything. Cleanup runs before the next effect, so clearing here gives the
      same "start fresh for this path" behaviour without the extra pass, and
      without leaving the previous file's error showing while the next one loads.
    */
    return () => {
      live = false;
      setUrl(undefined);
      setFailure(undefined);
      if (objectUrl !== undefined) URL.revokeObjectURL(objectUrl);
    };
    // `attempt` is `retry`'s whole mechanism: bumping it re-runs this effect,
    // which cleans up the previous failure and reads again (F15).
  }, [workspaceId, path, enabled, type, transform, attempt]);

  return {
    url,
    failed: failure !== undefined,
    failureReason: failure?.reason,
    error: failure?.error,
    retry,
  };
}
